import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { EnvelopeSimple, SignOut } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { resendVerificationEmail } from '../utils/emailVerification';

/* Global sandbox page: any authenticated account with email_verified===false
   is redirected here by ProtectedRoute for ANY protected route. Only two
   actions are available — resend the verification email, or log out. */
export const VerificationRequiredPage = () => {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (loggedOut || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.email_verified !== false) {
    return <Navigate to="/" replace />;
  }

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerificationEmail();
      toast.success(t('emailVerify.resendSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('emailVerify.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    setLoggedOut(true);
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6" data-testid="verification-required-page">
      <div className="bg-white border border-amber-300 rounded-xl p-8 text-center max-w-md w-full">
        <div className="text-2xl font-bold text-primary mb-6" data-testid="verification-required-logo">ATÔ</div>
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
          <EnvelopeSimple size={28} weight="fill" className="text-amber-600" />
        </div>
        <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-2" data-testid="verification-required-title">
          {t('emailVerify.actionBlocked')}
        </h1>
        <p className="text-text_muted mb-2 text-sm" data-testid="verification-required-email">
          {user.email}
        </p>
        <p className="text-text_muted mb-6 text-sm">{t('emailVerify.bannerMessage')}</p>

        <Button
          variant="primary"
          className="w-full py-3 font-semibold"
          disabled={resending}
          onClick={handleResend}
          data-testid="verification-required-resend-btn"
        >
          {resending ? t('common.loading') : t('emailVerify.resendButton')}
        </Button>
        <Button
          variant="ghost"
          className="w-full mt-2 flex items-center justify-center gap-2"
          disabled={loggingOut}
          onClick={handleLogout}
          data-testid="verification-required-logout-btn"
        >
          <SignOut size={18} />
          {t('emailVerify.logoutButton')}
        </Button>
      </div>
    </div>
  );
};
